import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import UseTranslationHook from "../hooks/UseTranslationHook";
import { cancelIconBlack } from "../assets/icons/SvgIconList";

export interface IOption<T> {
  label: string;
  value: T;
}

interface ICustomDropdownProps<T> {
  options: IOption<T>[];
  onSelect: (options: IOption<T>[]) => void;
  className?: string;
  selected?: IOption<T>[];
  searchable?: boolean;
  disabled?: boolean;
  label?: string;
  errorMessage?: string;
  defaultOption?: IOption<T>;
  required?: boolean;
}

const ICMultiSelectDropDown = <T extends Object>({
  options,
  onSelect,
  className,
  selected = [],
  searchable,
  disabled,
  label,
  errorMessage,
  defaultOption,
  required,
}: ICustomDropdownProps<T>) => {
  const { t } = UseTranslationHook();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [contentTopDown, setContentTopDown] = useState(false);
  const wrapperRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectOption = (option: IOption<T>) => {
    const isSelected = selected.some((item) => item.value === option.value);
    if (isSelected) {
      const updatedSelected = selected.filter(
        (item) => item.value !== option.value
      );
      onSelect(updatedSelected);
    } else {
      onSelect([...selected, option]);
    }
  };

  const getSelectedOption = () => {
    return selected.map((option) => option.label).join(", ");
  };
  const filteredOptions = useMemo(() => {
    return options.filter((item) =>
      item?.label?.toLowerCase().includes(searchText?.toLowerCase())
    );
  }, [searchText, options]);

  return (
    <div>
      <div
        className={`custom-dropdown-container ${className}`}
        ref={wrapperRef}
      >
        <div
          onClick={toggleDropdown}
          className={`relative dropdown-button ${
            errorMessage ? "error-border" : ""
          } ${isOpen ? "open" : ""}`}
        >
          {label && (
            <span
              className={` absolute top-0 ${
                getSelectedOption() === ""
                  ? "flex  items-center h-full typo-floating-label "
                  : "-translate-y-[60%] typo-floating-dropDown-label floating-dropDown-label"
              } bg-skin-drop-down-floating px-[4px] `}
            >
              {label + (required ? "*" : "")}
            </span>
          )}
          <div className="flex gap-2 multi-dropdown-option">
            {selected.map((option) => (
              <div className=" inline-block bg-skin-multi-select border border-skin-multi-selected text-skin-multi-selected rounded-xl p-0.5 mt-1">
                <div className="flex items-center gap-1">
                  {option.label}
                  {!disabled && (
                    <div onClick={() => handleSelectOption(option)}>
                      {cancelIconBlack}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {isOpen ? (
            <span className="arrow-dropdown-group">&#x25B4;</span>
          ) : (
            <span className="arrow-dropdown-group">&#9662;</span>
          )}
        </div>
        {isOpen && (
          <div
            className="multi-select-dropdown-content-option hide-scrollbar"
            style={{
              top: contentTopDown ? "" : "100%",
              bottom: contentTopDown ? "100%" : "",
              borderTop: contentTopDown ? "" : "0px",
              borderBottom: contentTopDown ? "0px" : "",
            }}
          >
            <div>
              {searchable && isOpen && (
                <input
                  type="text"
                  className="search-dropdown-custom-input typo-input-placeholder"
                  placeholder={t("search") + "..."}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  autoFocus
                />
              )}
            </div>
            <div className="max-h-48 overflow-auto hide-scrollbar">
              {[
                ...(defaultOption
                  ? [defaultOption]
                  : [{ label: "", value: "" }]),
                ...filteredOptions,
              ].map((option, index) => (
                <div
                  key={index}
                  className={`option typo-input-value  ${
                    option.label ? "" : "h-8"
                  } ${
                    selected.some((item) => item.value === option.value)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleSelectOption(option as IOption<T>)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {errorMessage && <div className="typo-error">{errorMessage}</div>}
    </div>
  );
};

export default ICMultiSelectDropDown;
